/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { push, replace } from 'connected-react-router';
// redux
import {fetchInvoice, fetchInvoices, updateDisplayInvoice as saveDisplayInvoice} from "../../../../../state/thunks/account";
import {toggleIsOpened, updateDisplayInvoice} from "../../../../../state/slices/account";
// material
import {SearchBar, InlineMessage, Icon, ToolTip, Switch} from '@insticator/insticator-ui'
import ExpandableSection from "../../../../../material/expandablesection/expandablesection";
import ConfigController from "../../../../../material/expandablesection/controllers/configcontroller/configcontroller";
import SectionCard from "../../../../../material/expandablesection/sectioncard/sectioncard";
import ExpandableListTable from "../../../../../material/expandablelisttable/expandablelisttable";
import InvoicePreview from "./invoicepreview";
import InvoiceManagement from "../advanced/invoiceManagement";
// consts
import {INVOICE_COL_HEADS} from "../billing_const";
// styles
import injectSheet from 'react-jss';
import styles from './invoice_styles';
import {resetToDB, updateUpdateStatus} from "../../../../../state/slices/settings";
import {
  updateInvoiceConfig
} from "../../../../../state/thunks/settings";
import SectionGroupDescription
  from "../../../../../material/expandablesection/sectiongroupdescription/sectiongroupdescription";

// static
const UPDATE_STATUS = 'advancedUpdateStatus'
const STATUS_IDLE = 'idle'
const STATUS_PENDING = 'pending'
const SUCCESS_MESSAGE = 'Update Success'
const ERROR_MESSAGE = 'Update Failed'


/* ========== ~~~~~~~~~~ INVOICE ~~~~~~~~~~ ========== */
const Invoice = injectSheet(styles)((props) => {
  const dispatch = useDispatch();
  const {
    location: {
      query
    }
  } = useSelector((state) => state.router);
  const {
    session: {
      accountUUID,
      mocked,
    },
    account: {
      invoices,
      invoicesFromDB,
      invoiceUpdateStatus
    },
    settings: {
      invoiceConfig,
      invoiceConfigFromDB,
      advancedUpdateStatus
    },
    account
  } = useSelector((state) => state)
  const permissions = useSelector((state) => state.session.permissions);
  const hasManageInvoiceDisplayPermission = permissions.includes('settings.manageInvoiceDisplay');

  useEffect(() => {
    if (advancedUpdateStatus === 'success' || advancedUpdateStatus === 'error') setTimeout(() => {
      dispatch(updateUpdateStatus({ field: UPDATE_STATUS, status: STATUS_IDLE }))
    }, [2000])
  }, [advancedUpdateStatus])

  useEffect(() => {
    if (invoiceConfigFromDB) {
      setIsChanged(isDifferentFromDB(invoiceConfig))
    }
  }, [invoiceConfig, invoices])

  const isDifferentFromDB = config => {
    let res = config.showInvoices !== invoiceConfigFromDB.showInvoices || config.enableVat !== invoiceConfigFromDB.enableVat || config.currencyCode !== invoiceConfigFromDB.currencyCode
    if (config.enableVat) {
      res = res || config.vatRate !== invoiceConfigFromDB.vatRate || config.vatRegistrationNumber !== invoiceConfigFromDB.vatRegistrationNumber
    }
    // check if invoices vs invoicesFromDB are different
    res = res || invoices.some(invoice => {
      const id = invoice.id
      return invoice.displayInvoice !== invoicesFromDB.find(invoice => invoice.id === id).displayInvoice
    })
    return res
  }

  const [isOpened, setIsOpened] = useState(false);
  const [filter, setFilter] = useState("");
  const [isChanged, setIsChanged] = useState(false);
  const yearQuery = "year";

  // Redirect to current year if none specified
  if (!query[yearQuery]) {
    dispatch(replace(`?${yearQuery}=${new Date().getFullYear()}`));
  }

  /*************** GENERAL FUNCTIONALITY ***************/
  const getYear = () => {
    return query[yearQuery] ? parseInt(query[yearQuery]) : new Date().getFullYear()
  }

  const decrementYear = () => {
    if (getYear() === 2022) return
    dispatch(push(`?${yearQuery}=${getYear() - 1}`))
    const year = getYear() - 1
    dispatch(fetchInvoices({ accountUUID, year }))
  }

  const incrementYear = () => {
    if (getYear() === new Date().getFullYear()) return
    dispatch(push(`?${yearQuery}=${getYear() + 1}`))
    const year = getYear() + 1
    dispatch(fetchInvoices({ accountUUID, year }))
  }

  const handleToggle = () => {
    setIsOpened(!isOpened)
  }
  /*************** INVOICE DOWNLOAD / PREVIEW ***************/
  const handleDownload = date => {
    if (!account[date]?.hasOwnProperty('blob')) return
    const invoiceBlob = account[date].blob
    const blob = new Blob([invoiceBlob], {type: "application/pdf"})
    const link = document.createElement('a')
    link.href = window.URL.createObjectURL(blob)
    const fileName = "report-" + date + ".pdf"
    link.download = fileName
    link.click()
  }

  const getInvoiceConfigRequestData = () => {
    let newInvoiceConfig = Object.assign({}, invoiceConfig)
    // Blanks default to 0
    newInvoiceConfig.vatRate = newInvoiceConfig.vatRate === ""
        ? 0.00
        : newInvoiceConfig.vatRate / 100.0
    // Don't update VAT configurations if VAT is disabled
    if (!newInvoiceConfig.enableVat) {
      delete newInvoiceConfig.vatRate
      delete newInvoiceConfig.vatRegistrationNumber
    }

    return {
      accountUUID: accountUUID,
      settings: newInvoiceConfig
    }
  }

  /*************** CONTROLLER & COMPONENT ***************/
  let statusMessage = "";
  switch (advancedUpdateStatus) {
    case "success":
      statusMessage = SUCCESS_MESSAGE
      break;
    case "failed":
      statusMessage = ERROR_MESSAGE
      break;
    case "idle":
      statusMessage = ""
      break;
  }

  const handlePreview = date => {
    if (!account[date]?.hasOwnProperty('pdfBase64')) return
    dispatch(toggleIsOpened(date))
  }

  const handleToggleDisplayInvoice = invoiceId => {
    dispatch(updateDisplayInvoice(invoiceId))
  }

  const handleSave = async () => {
    setIsChanged(false)
    setIsOpened(false)
    dispatch(updateUpdateStatus({ field: UPDATE_STATUS, status: STATUS_PENDING }))
    await dispatch(updateInvoiceConfig(getInvoiceConfigRequestData()))
    await dispatch(saveDisplayInvoice(invoices))
  }

  const handleCancel = () => {
    setIsOpened(false)
    dispatch(resetToDB({ cache: 'invoiceConfig', db: 'invoiceConfigFromDB'}))
  }

  useEffect(() => {
    if (!invoices.length) return
    invoices.forEach(invoice => {
      const date = invoice.date
      if (invoice.displayInvoice) dispatch(fetchInvoice({ accountUUID, date }))
    })
  }, [invoices])

  /*************** INVOICE FORMATTING ***************/
  const formattedInvoices = () => {
    if (Object.keys(invoices).length === 0) {
      return [];
    }

    let filtered = invoices.filter(invoice => Object.keys(invoice).some(k => invoice[k].toString().toLowerCase().includes(filter.toLowerCase())));
    if (!mocked || !hasManageInvoiceDisplayPermission) filtered = filtered.filter(invoice => invoice.displayInvoice)
    return filtered.map(invoice => {
      const data = {
        invoiceNumber: invoice.invoiceNo,
        invoiceDate: invoice.formattedDate,
        invoiceTotal: invoice.formattedTotal,
        action: <div className={'justify-content-center'}>
          <Icon name={'download'} klass={props.classes.downloadIcon} handleClick={() => handleDownload(invoice.date)}/>
          <ToolTip
              children={<Icon name={'eye'} klass={props.classes.icon} handleClick={() => handlePreview(invoice.date)}/>}
              position={"bottom"}
              content={"View"}
          />
        </div>,
        expandable: <InvoicePreview uuid={invoice.date}
                                    invoiceNo={invoice.invoiceNo}
                                    handleDownload={() => handleDownload(invoice.date)}
                                    handleClose={() => handlePreview(invoice.date)}/>
      }

      if (mocked && hasManageInvoiceDisplayPermission) {
        data.display = <div className="flexHorizontal">
          <Switch
              variant={'primary'}
              handleClick={() => handleToggleDisplayInvoice(invoice.id)}
              on={invoice.displayInvoice}
          />
        </div>
      }

      return data;
    })
  }

  const controller = (
    <ConfigController
      handleToggle={handleToggle}
      handleCancel={handleCancel}
      isOpened={isOpened}
      isChanged={isChanged}
      configTitle={'View'}
      cancelTitle={'Close'}
      title='Invoices'
      description={statusMessage}
      descriptionClass={(advancedUpdateStatus === "success" && invoiceUpdateStatus === "success")? props.classes.updateSuccess : props.classes.updateFailed}
    />
  );

  /*if logged in as publisher and not show invoices then return nothing */
  if(!mocked && !invoiceConfig.showInvoices) return null;

  return (
    <ExpandableSection
      className={`invoice ${props.classes.invoice}`}
      controller={controller}
      isOpened={isOpened}
      handleCancel={handleCancel}
      cancelTitle={'Cancel/Close'}
      handleSave={handleSave}
      isChanged={isChanged}
    >
        {isOpened && invoiceConfig.showInvoices && <>
          <SectionCard>
            <div className="navbar">
              <SearchBar
                  className="searchbar"
                  onChange={newFilter => setFilter(newFilter)}
              />
              <div className="navbar-right">
                <InlineMessage
                    className="message"
                    variant={'system'}
                    message={'All revenue reported in U.S. Dollars ($)'}
                    close={true}
                    handleClose={() => null}
                />
                <Icon name={'angle-left'} klass={props.classes.icon} handleClick={decrementYear}/>
                &nbsp;&nbsp;
                <span className="yearNav">{getYear()}</span>
                &nbsp;&nbsp;
                <Icon name={'angle-right'} klass={props.classes.icon} handleClick={incrementYear}/>
              </div>
            </div>
            <ExpandableListTable
                columnHeads={mocked && hasManageInvoiceDisplayPermission ? INVOICE_COL_HEADS : INVOICE_COL_HEADS.filter(col => col.key !== 'display')}
                data={formattedInvoices()}
                expandableKlass={'centerContent'}
            />
          </SectionCard>
        </>}
      {mocked && hasManageInvoiceDisplayPermission && <InvoiceManagement/>}
    </ExpandableSection>
  )
});

/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export default Invoice;